import { computed, type Ref } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { ProjectSide, ProjectStatus, RawQuoteRecord } from "@/v2Projects/types";

const TERMINAL_STATUSES: ProjectStatus[] = ["completed", "cancelled", "rejected"];
type UpdatableProjectStatus = Exclude<ProjectStatus, "quoted">;

export interface ProjectStatusUpdate {
  status: UpdatableProjectStatus;
  rejectionReason?: string;
  autoReviewRequestsEnabled?: boolean;
}

function hasPaidWork(quotes: RawQuoteRecord[]): boolean {
  return quotes.some((quote) =>
    quote.payment_status === "paid" || quote.payment_status === "partial_paid",
  );
}

function isClientCancellableStatus(status: ProjectStatus): boolean {
  return status === "pending" || status === "quoted" || status === "in_progress";
}

export function useProjectStatus(
  projectId: Ref<number>,
  side: Ref<ProjectSide>,
  actingOrgId: Ref<number>,
  projectStatus: Ref<ProjectStatus | null | undefined>,
  quotes: Ref<RawQuoteRecord[]>,
) {
  const api = useApi();
  const queryClient = useQueryClient();

  const projectsApi = computed(() =>
    side.value === "expert" ? api.expert.projectsV2 : api.client.projectsV2,
  );

  const hasPaidQuote = computed(() => hasPaidWork(quotes.value));

  const allowedTransitions = computed<UpdatableProjectStatus[]>(() => {
    const currentStatus = projectStatus.value;
    if (!currentStatus || TERMINAL_STATUSES.includes(currentStatus)) {
      return [];
    }

    if (side.value === "expert") {
      switch (currentStatus) {
        case "pending":
        case "quoted":
          return ["in_progress", "rejected"];
        case "in_progress":
          return ["submitted"];
        default:
          return [];
      }
    }

    const next: UpdatableProjectStatus[] = [];

    if (currentStatus === "submitted") {
      next.push("completed");
    }

    if (!hasPaidQuote.value && isClientCancellableStatus(currentStatus)) {
      next.push("cancelled");
    }

    return next;
  });

  const updateProjectStatus = useMutation({
    mutationFn: async ({ status, rejectionReason, autoReviewRequestsEnabled }: ProjectStatusUpdate) =>
      projectsApi.value.updateStatus({
        params: { projectId: projectId.value },
        body: {
          acting_org_id: actingOrgId.value,
          project_status: status,
          ...(rejectionReason ? { rejection_reason: rejectionReason } : {}),
          ...(autoReviewRequestsEnabled === undefined ? {} : { auto_review_requests_enabled: autoReviewRequestsEnabled }),
        },
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["v2projects", "detail", projectId.value],
        }),
        queryClient.invalidateQueries({
          queryKey: ["v2projects", "quotes", projectId.value],
        }),
        queryClient.invalidateQueries({
          queryKey: ["v2projects", "review"],
        }),
      ]);
    },
  });

  return {
    allowedTransitions,
    hasPaidQuote,
    updateProjectStatus,
  };
}
